"use client"

import { FeedData as Feed } from "@extractus/feed-extractor"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"

interface PostDescriptionProps {
  posts: any[]
  feed: Feed
}

const POSTS_PER_PAGE = 10

export function PostDescription({ posts, feed }: PostDescriptionProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE)

  // Calculate pagination range
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE
  const endIndex = Math.min(startIndex + POSTS_PER_PAGE, posts.length)
  const currentPosts = posts.slice(startIndex, endIndex)

  // Calculate page numbers to show (1,2,3,etc)
  const MAX_PAGE_BUTTONS = 5
  let pageNumbers: (number | string)[] = []

  if (totalPages <= MAX_PAGE_BUTTONS) {
    // Show all pages if there are few
    pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
  } else {
    // Show first, last, current, and some surrounding pages
    pageNumbers.push(1)

    if (currentPage > 2) {
      pageNumbers.push(currentPage === 3 ? 2 : "...")
    }

    if (currentPage !== 1 && currentPage !== totalPages) {
      pageNumbers.push(currentPage)
    }

    if (currentPage < totalPages - 1) {
      pageNumbers.push(currentPage === totalPages - 2 ? totalPages - 1 : "...")
    }

    pageNumbers.push(totalPages)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-base font-semibold">
          Ready to import {posts.length} posts from {feed.title || "RSS feed"}
        </h3>
        <p className="text-sm text-muted-foreground">
          We've processed your posts and they're ready to import. You can
          customize the import options below.
        </p>
      </div>

      {posts.length > 0 && (
        <>
          <div className="space-y-1">
            <p className="text-sm font-medium">Posts to import:</p>
            <ul className="pl-6 text-sm list-disc space-y-1 pr-2">
              {currentPosts.map((post, i) => (
                <li key={i + startIndex} className="text-muted-foreground">
                  {post.title || "Untitled post"}
                </li>
              ))}
            </ul>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col items-start space-y-2 pt-1 pl-16">
              <span className="text-xs text-muted-foreground">
                Showing {startIndex + 1}-{endIndex} of {posts.length}
              </span>

              <div className="flex items-center space-x-1 text-sm">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {pageNumbers.map((pageNum, i) =>
                  typeof pageNum === "number" ? (
                    <Button
                      key={i}
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      className="h-7 w-7 px-0"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ) : (
                    <span key={i} className="px-1">
                      ...
                    </span>
                  )
                )}

                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
